import { Address, MarketAssetMeta, makeUsd, PointsItem } from "@silo/types";
import { Effect, Option, pipe } from "effect";
import { getTokenLabels } from "../utils/get-token-labels";
import { ContractsClient } from "../contracts-client";
import { RawDashboardMarketAsset } from "./types";
import {
  getConfigDepositMode,
  getConfigIsDebtAprFixed,
  getConfigPointsByType,
  getCurveLpMeta,
  getMarketConfig,
} from "@silo/config";
import { convertToUsd, getIsDisabledCollateral } from "@silo/utils";

export type GetRawDashboardMarketAsset = ReturnType<
  typeof getRawDashboardMarketAsset
>;
export type GetRawDashboardMarketAssetSuccess =
  Effect.Effect.Success<GetRawDashboardMarketAsset>;
export type GetRawDashboardMarketAssetError =
  Effect.Effect.Error<GetRawDashboardMarketAsset>;
export type GetRawDashboardMarketAssetContext =
  Effect.Effect.Context<GetRawDashboardMarketAsset>;

interface GetRawDashboardMarketAssetParams {
  marketAssetMeta: MarketAssetMeta;
  user: Address;
}

export function getRawDashboardMarketAsset({
  marketAssetMeta,
  user,
}: GetRawDashboardMarketAssetParams) {
  return Effect.gen(function* () {
    const {
      protocolKey,
      tokenAddress,
      assetKey,
      chainKey,
      decimals,
      marketAddress,
      maxLtv,
      lt,
      protectedShareToken,
    } = marketAssetMeta;

    const { curveLpTokenAddress } = getCurveLpMeta(marketAssetMeta);
    const effectiveTokenAddress = curveLpTokenAddress ?? tokenAddress;

    const [
      {
        priceNative,
        priceUsd,
        collateralBaseApr,
        collateralRewardsApr,
        collateralTotal,
        protectedRewardsApr,
        protectedTotal,
        debtBaseApr,
        debtRewardsApr,
        debtTotal,
        isPaused,
      },
      { collateralBalance, protectedBalance, debtBalance },
    ] = yield* Effect.all(
      [
        ContractsClient.getMarketAssetState(marketAssetMeta),
        ContractsClient.getMarketAssetUserState({
          user,
          marketAddress,
          tokenAddress,
          protectedShareToken,
          effectiveTokenAddress,
        }),
      ],
      { concurrency: "unbounded" }
    );

    const { symbol, name } = yield* getTokenLabels(marketAssetMeta);

    const { collateralPoints, protectedPoints, debtPoints } =
      getConfigPointsByType({
        protocolKey,
        marketAddress,
        assetKey,
      });

    const isDebtAprFixed = getConfigIsDebtAprFixed({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const isDisabledCollateral = getIsDisabledCollateral(maxLtv);

    const collateralBalanceUsd = convertToUsd(
      collateralBalance,
      decimals,
      priceUsd
    );

    const protectedBalanceUsd = convertToUsd(
      protectedBalance,
      decimals,
      priceUsd
    );

    const debtBalanceUsd = convertToUsd(debtBalance, decimals, priceUsd);

    const depositBalanceUsd = pipe(
      collateralBalanceUsd + protectedBalanceUsd,
      makeUsd
    );

    const depositMode = getConfigDepositMode({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const dashboardMarketAsset: RawDashboardMarketAsset = {
      assetKey,
      protocolKey,
      maxLtv,
      lt,

      priceNative,
      priceUsd,

      collateralBaseApr,
      collateralRewardsApr,
      collateralPoints,
      collateralTotal,

      protectedRewardsApr,
      protectedPoints,
      protectedTotal,

      debtBaseApr,
      debtRewardsApr,
      debtPoints,
      debtTotal,

      isDebtAprFixed,
      isDisabledCollateral,
      isPaused,
      depositMode,

      tokenAddress,
      chainKey,
      symbol,
      name,
      decimals,

      collateralBalance,
      collateralBalanceUsd,
      protectedBalance,
      protectedBalanceUsd,
      depositBalanceUsd,
      debtBalance,
      debtBalanceUsd,
    };

    return dashboardMarketAsset;
  });
}
